import { useEffect, useState, useSyncExternalStore } from "react";
import {
  sanitizeUiSettings,
  resolveMode,
  uiVisible,
  DEFAULT_SITE_MODE,
  type ResolvedMode,
  type UiFeatureDoc,
  type UiSettings,
} from "@greg-chick/shared";
import { classMode } from "./classMode";
import { getDeviceMode, subscribeDeviceMode } from "./deviceMode";
import { EDGE_BASE } from "./apiBase";
import { fetchEarlyOr } from "./earlyFetch";
import { subscribeContentUpdates } from "./liveReload";

/**
 * 🎚️ **어디에 쓰느냐에 따라 켜고 끈 설정**을 가져온다.
 *
 * 🔴 **못 받아 오면 「아무것도 안 골랐다」로 본다** — 그러면 코드 기본값(보이기)이 쓰인다.
 * 서버가 죽었다고 아이 화면에서 메뉴가 사라지면 안 된다(불변식 3).
 * 반대로 두면 인터넷이 잠깐 끊긴 순간 화면이 통째로 비어 「고장」으로 보인다.
 *
 * 🔴 **기다리지 않는다.** 첫 그림은 기본값으로 그리고, 설정이 오면 그때 바뀐다.
 * 기다리게 하면 첫 화면이 늦고, 그건 이 서비스에서 제일 비싼 지연이다.
 */
/**
 * 🔴 **엣지의 기본값과 같아야 한다**(둘 다 `shared` 의 `DEFAULT_SITE_MODE`, 2026-09-25).
 *
 * 여기가 `auto` 이고 운영 기본이 `kids` 이면, 첫 그림은 메뉴 **일곱**으로 그려지고 설정이
 * 온 순간 **넷**으로 줄어든다 — 아이가 보는 앞에서 화면이 한 번 접힌다. 그 깜빡임은
 * 「고장」으로 읽힌다. `uiDefaultsParity.test.ts` 가 둘을 맞춰 준다.
 *
 * 숨는 것이 아니라 **옮기는 것**이라 불변식 3 과도 어긋나지 않는다 — 영유아 모드에서도
 * 아이 동선(전자책·마법·체험공간)은 그대로고, 나머지는 하단 [🔒 보호자] 칸으로 간다.
 */
const DEFAULTS: UiSettings = { mode: DEFAULT_SITE_MODE, features: {} };

let cache: UiSettings | null = null;
let inflight: Promise<UiSettings> | null = null;

export async function loadUiSettings(): Promise<UiSettings> {
  if (cache) return cache;
  if (inflight) return inflight;
  inflight = (async () => {
    try {
      // 책 화면에서는 index.html 이 먼저 띄워 둔 것을 받아 쓴다(없으면 직접 · lib/earlyFetch)
      const res = await fetchEarlyOr(`${EDGE_BASE}/api/ui-features`, { cache: "no-store" });
      if (!res.ok) return DEFAULTS; // 미게시 — 다 보인다(캐시하지 않는다)
      cache = sanitizeUiSettings(await res.json()); // 응답도 밖에서 온 값이다(불변식 7)
      return cache;
    } catch {
      return DEFAULTS; // 오프라인 — 다 보인다(캐시하지 않는다: 붙으면 다시 시도)
    } finally {
      inflight = null;
    }
  })();
  return inflight;
}

export async function loadUiFeatures(): Promise<UiFeatureDoc> {
  return (await loadUiSettings()).features;
}

/** 테스트·게시 직후용. */
export function resetUiFeatures(): void {
  cache = null;
}

/**
 * 🗺 **React 밖에서 묻는 「지금 보이나」** — 체험공간(Phaser)처럼 훅을 못 쓰는 곳용.
 * 판정은 `useMode` 와 같다(`resolveMode` + `uiVisible`). 설정을 아직 못 받았으면 기본값(영유아)으로
 * 본다 — 아이에게 더 안전한 쪽이다.
 */
export function uiVisibleNow(key: string): boolean {
  const s = uiSettingsNow();
  const m = resolveMode(s.mode, classMode(), getDeviceMode());
  return uiVisible(key, s.features, m.inClass, m.kids);
}

/** 지금까지 받아 둔 설정(없으면 기본) — 첫 그림을 기다리지 않게. */
export function uiSettingsNow(): UiSettings {
  return cache ?? DEFAULTS;
}

export function uiFeaturesNow(): UiFeatureDoc {
  return uiSettingsNow().features;
}

/**
 * 화면에서 쓰는 훅. 운영자가 게시하면 **열려 있는 화면도 바로** 바뀐다 —
 * 새로고침을 시켜서는 안 된다(운영자는 「안 바뀐다」로 겪는다).
 */
export function useUiSettings(): UiSettings {
  const [doc, setDoc] = useState<UiSettings>(uiSettingsNow);
  useEffect(() => {
    let alive = true;
    const pull = () => void loadUiSettings().then((d) => alive && setDoc(d));
    pull();
    const off = subscribeContentUpdates((up) => {
      if (up.type !== "uifeatures") return;
      resetUiFeatures();
      pull();
    });
    return () => {
      alive = false;
      off();
    };
  }, []);
  return doc;
}

export function useUiFeatures(): UiFeatureDoc {
  return useUiSettings().features;
}

/**
 * 🔴 **지금 수업 모드인가 — 화면은 이것만 부른다.**
 *
 * 운영자가 고른 모드(`class`/`home`)와 이 탭이 수업 링크로 들어왔는지(`?class=1`)를
 * 합치는 자리다. **판정은 `shared/inClassNow` 한 곳**이고 여기는 그것을 부르기만 한다.
 * 화면마다 다시 짜면 아무 에러 없이 갈린다 — 하단 메뉴는 수업인데 그림책은 아니라거나.
 */
export function useInClass(): boolean {
  return useMode().inClass;
}

/**
 * 🧸 **지금 영유아 모드인가 — 화면은 이것만 부른다.**
 *
 * 판정은 `shared/resolveMode` 한 곳이고 여기는 부르기만 한다(`useInClass` 와 같은 이유).
 */
export function useKids(): boolean {
  return useMode().kids;
}

/**
 * 🔴 **지금 어떤 화면인가 — 입력 셋을 한 곳에서 합친다.**
 *
 * 학교(선생님 링크·전용 설정) · 이 기기에서 보호자가 고른 것 · 운영자 게시 설정.
 * 셋이라 화면마다 다시 짜면 **아무 에러 없이 갈린다** — 판정은 `shared/resolveMode` 하나다.
 */
export function useMode(): ResolvedMode {
  const settings = useUiSettings();
  // 보호자가 이 기기에서 바꾸면 **열려 있는 화면도** 바뀌어야 한다(안 그러면 「안 바뀐다」로 겪는다)
  const device = useSyncExternalStore(subscribeDeviceMode, getDeviceMode, getDeviceMode);
  return resolveMode(settings.mode, classMode(), device);
}
